import React, { useState } from "react";

import { isEmpty } from "lodash/fp";

import IntegrationChannelSelector from "@app/analytics/components/IntegrationChannelSelector";
import useTeamsConversations from "@app/analytics/hooks/integrations/useTeamsConversations";
import useSendTopicReport from "@app/analytics/TopicReport/hooks/useSendTopicReport";
import type { TeamsConversation } from "@app/analytics/types/teamsTypes";
import {
  ContentContainer,
  Footer,
} from "@app/common/access/ShareReport/styles";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";

import Alert from "@rui/Alert";
import AppProviderButton from "@rui/AppProviderButton";
import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import AddAMessage from "./AddAMessage";
import type { DialogContentProps } from "./types";

const SendToTeamsContent = ({
  onClose,
  selectedEntityKinds,
  topicId,
}: DialogContentProps) => {
  const [message, setMessage] = useState("");
  const [selectedChannel, setSelectedChannel] =
    useState<TeamsConversation | null>(null);

  const { conversations } = useTeamsConversations();
  const { shareTopicReportToTeams } = useSendTopicReport();

  const handlePostToTeams = () => {
    if (!selectedChannel) return;

    shareTopicReportToTeams.mutate(
      {
        conversationId: selectedChannel.id,
        conversationKind: selectedChannel.kind,
        includeEntities: selectedEntityKinds,
        message,
        teamId: selectedChannel.teamId,
        topicId,
      },
      {
        onSuccess: () => {
          toast.success("Your topic has been posted to Teams!");
          onClose();
        },
      }
    );
  };

  const submitDisabled = !selectedChannel || isEmpty(selectedEntityKinds);

  return (
    <>
      <ContentContainer spacing={2}>
        <Stack paddingY={2}>
          <IntegrationChannelSelector
            channels={conversations}
            onChange={setSelectedChannel}
            selected={selectedChannel}
          />
          <Stack paddingTop={1.5} spacing={1}>
            <AddAMessage onChange={setMessage} />
            {shareTopicReportToTeams.isError && (
              <Alert
                severity="error"
                embedded
                alignItems="flex-start"
                sx={{ border: "1px solid #D32F2F" }}
              >
                <Bold>Error</Bold>
                <br />
                There was an error posting to Teams. Please try again or contact{" "}
                <Link href={LINKS.EMAIL.SUPPORT}>support@read.ai</Link> if the
                problem persists.
              </Alert>
            )}
          </Stack>
        </Stack>
      </ContentContainer>
      <Footer>
        <Row variant="right" flex={1}>
          <TooltipWrapper
            placement="top"
            show={submitDisabled}
            title="Please select a channel"
          >
            <AppProviderButton
              provider="microsoft_graph_teams_push"
              onClick={handlePostToTeams}
              disabled={submitDisabled}
              loading={shareTopicReportToTeams.isPending}
            >
              Post to Teams
            </AppProviderButton>
          </TooltipWrapper>
        </Row>
      </Footer>
    </>
  );
};

export default SendToTeamsContent;
